import { LuEllipsisVertical } from '@react-icons/all-files/lu/LuEllipsisVertical'
import { LuMessageSquareText } from '@react-icons/all-files/lu/LuMessageSquareText'
import memoize from 'memoize-one'
import React, { useMemo } from 'react'
import { useMediaQuery } from 'react-responsive'
import type { Location } from 'react-router-dom'
import { useLocation, useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'

import type { ExplorerPane } from 'types/explorer'
import type { Group, GroupId } from 'types/group'
import type { PlaylistId } from 'types/playlist'
import type { Track } from 'types/track'

import NativeTagsMenu from 'components/common/native-tags-menu'
import { DropdownMenu, IconButton } from 'components/ds'
import ActionButton from 'components/ui/action-button'
import ButtonContainer from 'components/ui/button-container'
import Tooltip from 'components/ui/tooltip'

import TrackDropdown from './track-dropdown'

const getTrackIds = memoize(trackId => [trackId])
const EMPTY_ARRAY = []

type Props = {
  track: Track | null | undefined
  sourcePlaylistId?: PlaylistId | null | undefined
  sourceShortlistId?: string
  itemIndex?: number
  removeFromShortlist?: boolean
  customEditMetadataUrl?: string | null | undefined
  editMetadataModalOpen?: boolean
  allowSharing?: boolean
  downloadTrack?: boolean
  manageNesting?: boolean
  manageTrackFormats?: boolean
  editMetadata?: boolean
  permissionsAndSharing?: boolean
  addToShortlist?: boolean
  deleteTrack?: boolean
  addToCustomGroup?: boolean
  addToNewCustomGroup?: boolean
  group?: Group | null | undefined
  groupId?: GroupId | null | undefined
  isPlayer?: boolean
  buttonSize?: 'default' | 'compact'
  copyMetadata?: boolean
  versionsButton?: React.ReactNode
  hideEls?: (
    | 'native_tags'
    | 'versions_button'
    | 'context_menu'
    | 'share_shortcut'
    | 'notes_shortcut'
  )[]
  pane?: ExplorerPane
  onMenuOpen?: (...args: Array<any>) => any
  location?: Location
  mixpanelContext?:
    | 'Explorer'
    | 'Track pane'
    | 'Shortlist'
    | 'Player'
    | 'Browse'
    | 'Starred'
    | 'Inbox'
    | 'Channel'
    | 'Similar tracks'
    | 'Search results'
}

const TrackActions = (props: Props): null | React.ReactNode => {
  const {
    buttonSize,
    hideEls: hideElsProp,
    track,
    versionsButton,
    onMenuOpen,
    location: locationProp,
    mixpanelContext,
  } = props
  const routerLocation = useLocation()
  const location = locationProp || routerLocation
  const mobile = useMediaQuery({ maxWidth: 639 })
  const navigate = useNavigate()

  const hideEls = useMemo(() => {
    if (!track?.permissions?.manage)
      return [...(hideElsProp || []), 'native_tags']
    return hideElsProp || []
  }, [hideElsProp, track?.permissions?.manage])

  if (track?.tempId) return null

  const onShortcutClick = () => {
    if (track) {
      navigate(`/explorer/tracks/${track.id}/share`)
      mixpanel.sendPureEvent('Click icon', {
        'Track ID': track.id,
        Type: 'Track',
        Icon: 'Share',
        Context: mixpanelContext,
      })
    }
  }

  const onNotesShortcutClick = () => {
    if (!track) return
    const baseUrl =
      props.customEditMetadataUrl ??
      `/explorer/tracks/${track.id}/edit-metadata`
    navigate(`${baseUrl}?tab=notes`)
    mixpanel.sendPureEvent('Click icon', {
      'Track ID': track.id,
      Type: 'Track',
      Icon: 'Notes',
      Context: mixpanelContext,
    })
  }

  return (
    <ButtonContainer>
      {!hideEls?.includes('share_shortcut') && !mobile && (
        <>
          {track?.permissions?.share_link && (
            <ActionButton
              icon="share-arrow"
              onClick={onShortcutClick}
              title="Share"
              compact
              size="32"
            />
          )}
        </>
      )}
      {!hideEls?.includes('notes_shortcut') && (
        <Tooltip content="Add track note">
          <IconButton
            variant="ghost"
            highContrast
            aria-label="Track Notes"
            onClick={onNotesShortcutClick}
            size="1"
          >
            <LuMessageSquareText size="24" />
          </IconButton>
        </Tooltip>
      )}
      {!hideEls?.includes('native_tags') && (
        <div className="hidden-phone">
          <NativeTagsMenu
            buttonSize={buttonSize}
            trackIds={track?.id ? getTrackIds(track.id) : EMPTY_ARRAY}
            assignedTags={track?.native_tags || EMPTY_ARRAY}
            mixpanelContext={mixpanelContext}
          />
        </div>
      )}
      {!hideEls?.includes('versions_button') && versionsButton}
      {!hideEls?.includes('context_menu') && (
        <DropdownMenu.Root
          onOpenChange={open => {
            if (open && onMenuOpen) onMenuOpen()
            if (open && track) {
              mixpanel.sendPureEvent('Click icon', {
                'Track ID': track.id,
                Type: 'Track',
                Icon: 'Context menu',
                Context: mixpanelContext,
              })
            }
          }}
        >
          <DropdownMenu.Trigger>
            <IconButton
              variant="ghost"
              color="gray"
              highContrast
              size={buttonSize === 'compact' ? '1' : '2'}
              aria-label="More actions"
              data-testid={
                track?.id != null ? `track-context-menu-${track.id}` : undefined
              }
            >
              <LuEllipsisVertical size={20} />
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            {track && (
              <TrackDropdown
                {...props}
                track={track}
                location={location}
                closeMenu={() => {}}
              />
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      )}
    </ButtonContainer>
  )
}

export default TrackActions
export type TrackActionsType = typeof TrackActions
